import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { EndPoints } from '../../axios/endPoints';
import { axiosInstance } from '../../axios/axiosHandler';
import { authHeader } from '../auth-slice/auth-slice';

// Async thunk to upload a testing dataset file
export const UploadFile = createAsyncThunk(
    'UploadFile',
    async (formData: FormData, thunkAPI) => {
        try {
            // Send a POST request to upload the file
            const response = await axiosInstance.post(
                EndPoints.ML_TESTER_ADD_TESTING_DATASET,
                formData,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            // Handle failure by rejecting the action
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to delete a testing dataset by its ID
export const DeleteTestingDataset = createAsyncThunk(
    'DeleteTestingDataset',
    async (id: number, thunkAPI) => {
        try {
            // Send a POST request to delete the dataset
            const response = await axiosInstance.post(
                EndPoints.ML_TESTER_DELETE_TESTING_DATASET + `?id=${id}`,
                null,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch details of a testing dataset by its ID
export const GetTestingDatasetDetails = createAsyncThunk(
    'GetTestingDatasetDetails',
    async (id: number, thunkAPI) => {
        try {
            // Send a GET request to fetch dataset details
            const response = await axiosInstance.get(
                EndPoints.ML_TESTER_GET_TRAINING_FILE_DETAILS + `?id=${id}`,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch details of a specific training dataset by its ID
export const GetDatasetsCosmosDB = createAsyncThunk(
  "GetDatasetsCosmosDB",
  async ({ datasetId, pageNumber, pageSize }: {datasetId:number, pageNumber: number, pageSize: number} , thunkAPI) => {
    try {
      // Send a GET request to fetch dataset details
      const response = await axiosInstance.get(
        EndPoints.ML_TESTER_GET_TRAINING_FILE_DATA + `?datasetId=${datasetId}&&pageNumber=${pageNumber}&&pageSize=${pageSize}`,
        authHeader()
      );
      return await response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(null);
    }
  }
);

// Async thunk to perform an AI health check
export const AIHealthCheck = createAsyncThunk(
    'AIHealthCheck',
    async (_, thunkAPI) => {
        try {
            // Send a GET request for AI health check
            const response = await axiosInstance.get(
                EndPoints.ML_TESTER_AI_HEALTH_CHECK,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Interface to define the structure of an AI prediction request
export interface AIPredictModel {
    mlTestingDatasetId: number;
    mlResultName: string;
    mlTrainedModelId: number;
    inputColumns: string[];
}

// Async thunk to perform AI prediction
export const AIPredict = createAsyncThunk(
    'AIPredict',
    async (body: AIPredictModel, thunkAPI) => {
        try {
            // Send a POST request to perform prediction
            const response = await axiosInstance.post(
                EndPoints.ML_TESTER_AI_PREDICT,
                body,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to delete a prediction result by ID
export const DeletePredictionResult = createAsyncThunk(
    'DeletePredictionResult',
    async (id: number, thunkAPI) => {
        try {
            // Send a POST request to delete a prediction result
            const response = await axiosInstance.post(
                EndPoints.ML_TESTER_DELETE_PREDICTION_RESULT + `?id=${id}`,
                null,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Async thunk to fetch details of a prediction result by ID
export const GetPredictionResultDetails = createAsyncThunk(
    'GetPredictionResultDetails',
    async (id: number, thunkAPI) => {
        try {
            // Send a GET request to fetch prediction result details
            const response = await axiosInstance.get(
                EndPoints.ML_TESTER_GET_PREDICTION_RESULT_DETAILS + `?id=${id}`,
                authHeader()
            );
            return await response.data;
        } catch (error) {
            return thunkAPI.rejectWithValue(null);
        }
    }
);

// Create a Redux slice to manage ML tester-related state
export const mlTesterSlice = createSlice({
    name: 'mltester',
    initialState: {
        // Define initial state properties
        refreshMLTesterTable: 0,
        openImportTrainingDataDialog: false,
        chosenFile: null,
        chosenName: '',
        uploadingFile: false,
        deleteFilePopupOpen: false,
        deleteFileBodyParams: null,
        deletingFile: false,
        fileDetailsPopupOpen: false,
        fileDetailsBodyParams: null,
        fileDetailsLoading: false,
        fileDetails: null,
        fileDatasetPopupOpen: false,
        fileDatasetBodyParams: null,
        fileDatasetLoading: false,
        fileDataset: null as any,
        selectedTesterRow: null,
        newPredictionDialogOpen: false,
        newPredictionLoading: false,
        selectedColumn: null,
        selectedInputColumn: null,
        columns: [] as any[],
        inputColumns: [] as any[],
        performingAIHealthCheck: false,
        successfulNewPredictionOpen: false,
        successfulNewPredictionReponse: null,
        refreshSelectedMLTesterTable: 0,
        deletingPredictionResult: false,
        deletePredicitionResultDialogOpen: false,
        deletePredicitionResultBodyParams: null,
        predictionResultDetailsDialogOpen: false,
        predictionResultDetailsDialogBodyParams: null,
        predictionResultDeatilsLoading: false,
        predictionResultDetails: null,
    },
    reducers: {
        // Toggle import training data dialog visibility
        handleToggleImportTrainingDataDialog: (state, action) => {
            state.openImportTrainingDataDialog = action.payload;
            state.chosenFile = null;
            state.chosenName = '';
        },
        // Set the chosen file for upload
        handleSetChosenFile: (state, action) => {
            state.chosenFile = action.payload;
        },
        // Set the chosen file name
        handleSetChosenName: (state, action) => {
            state.chosenName = action.payload;
        },
        // Toggle delete file popup visibility
        handleToggleDeleteFilePopup: (state, action) => {
            state.deleteFilePopupOpen = action.payload.open;
            state.deleteFileBodyParams = action.payload.body;
        },
        // Toggle file details popup visibility
        handleToggleFileDetailsPopup: (state, action) => {
            state.fileDetailsPopupOpen = action.payload.open;
            state.fileDetailsBodyParams = action.payload.body;
            if (!action.payload.open) {
                state.fileDetails = null;
                state.columns = [];
            }
        },
        // Toggles the file data popup visibility and clears details on close
        handleToggleFileDatasetPopup: (state, action) => {
            state.fileDatasetPopupOpen = action.payload.open;
            state.fileDatasetBodyParams = action.payload.body;
            if (!action.payload.open) {
            state.fileDataset = null;
            state.columns = [];
            }
        },
        // Set the selected tester row
        handleTesterRowClick: (state, action) => {
            state.selectedTesterRow = action.payload;
        },
        // Handle new prediction dialog and reset prediction state
        handleNewPrediction: (state, action) => {
            state.columns = [];
            state.newPredictionDialogOpen = action.payload.isOpen;
            state.selectedColumn = null;
            state.selectedInputColumn = null;
            state.inputColumns = [];
        },
        // Set the selected column
        handleSelectColumn: (state, action) => {
            state.selectedColumn = action.payload;
            state.selectedInputColumn = null;
        },
        // Set the selected input column
        handleSelectInputColumn: (state, action) => {
            state.selectedInputColumn = action.payload;
            state.selectedColumn = null;
        },
        // Close successful new prediction dialog and refresh table
        handleCloseSuccessfulNewPredictionDetails: (state) => {
            state.successfulNewPredictionOpen = false;
            state.refreshSelectedMLTesterTable++;
            state.successfulNewPredictionReponse = null;
        },
        // Move a column to input columns
        handleMoveColumnToInput: (state, action) => {
            state.inputColumns.push({ ...action.payload });
            state.columns = state.columns.filter((el: any) => el.id !== action.payload.id);
            state.selectedColumn = null;
        },
        // Move a column back to available columns
        handleMoveColumnToColumns: (state, action) => {
            state.columns.push({ ...action.payload });
            state.inputColumns = state.inputColumns.filter((el) => el.id !== action.payload.id);
            state.selectedInputColumn = null;
        },
        // Move all columns to input columns
        handleMoveAllColumnsToInput: (state) => {
            state.inputColumns = [...state.inputColumns, ...state.columns];
            state.columns = [];
            state.selectedColumn = null;
        },
        // Move all input columns back to available columns
        handleMoveAllColumnsToColumns: (state) => {
            state.columns = [...state.columns, ...state.inputColumns];
            state.inputColumns = [];
            state.selectedInputColumn = null;
        },
        // Toggle delete prediction result dialog
        handleToggleDeletePredictionResult: (state, action) => {
            state.deletePredicitionResultDialogOpen = action.payload.isOpen;
            state.deletePredicitionResultBodyParams = action.payload.body;
        },
        // Toggle prediction result details dialog
        handleTogglePredictionResultDetailsDialog: (state, action) => {
            state.predictionResultDetailsDialogOpen = action.payload.isOpen;
            state.predictionResultDetailsDialogBodyParams = action.payload.body;
        },
        // Refresh ML tester table
        handleRefreshMLTesterTable: (state) => {
            state.refreshMLTesterTable++;
        },
        // Refresh selected tester table
        handleRefreshSelectedMLTesterTable: (state) => {
            state.refreshSelectedMLTesterTable++;
        },
    },
    extraReducers: (builder) => {
        // Handle cases for async thunks to manage state transitions
        builder.addCase(UploadFile.pending, (state) => { 
            state.uploadingFile = true;
        });
        builder.addCase(UploadFile.fulfilled, (state, action) => { 
            state.uploadingFile = false;
            if (action.payload.success) {
                state.openImportTrainingDataDialog = false;
                state.refreshMLTesterTable++;
            }
        });
        builder.addCase(UploadFile.rejected, (state) => { 
            state.uploadingFile = false;
        });

        builder.addCase(DeleteTestingDataset.pending, (state) => { 
            state.deletingFile = true;
        });
        builder.addCase(DeleteTestingDataset.fulfilled, (state) => { 
            state.deletingFile = false;
            state.deleteFilePopupOpen = false;
            state.deleteFileBodyParams = null;
            state.refreshMLTesterTable++;
        });
        builder.addCase(DeleteTestingDataset.rejected, (state) => { 
            state.deletingFile = false;
            state.deleteFilePopupOpen = false;
            state.deleteFileBodyParams = null;
            state.refreshMLTesterTable++;
        });

        builder.addCase(GetTestingDatasetDetails.pending, (state) => { 
            state.fileDetailsLoading = true;
        });
        builder.addCase(GetTestingDatasetDetails.fulfilled, (state, action) => { 
            state.fileDetailsLoading = false;
            if (action.payload.success) {
                const results = action.payload.result;
                const columns = results.columns.map((el: any, index: number) => ({
                    ...el,
                    id: index + 1,
                }));
                state.fileDetails = { ...results, columns };
                state.columns = columns;
            }
        });
        builder.addCase(GetTestingDatasetDetails.rejected, (state) => { 
            state.fileDetailsLoading = false;
            state.fileDetails = null;
        });

        builder.addCase(GetDatasetsCosmosDB.pending, (state) => {
            state.fileDatasetLoading = true;
        });
        builder.addCase(GetDatasetsCosmosDB.fulfilled, (state, action) => {
            state.fileDatasetLoading = false;
            if (action.payload.success) {
            state.fileDataset = action.payload.result;
            } else {
            state.fileDataset = null;
            }
        });
        builder.addCase(GetDatasetsCosmosDB.rejected, (state) => {
            state.fileDatasetLoading = false;
            state.fileDataset = null;
        });

        builder.addCase(AIHealthCheck.pending, (state) => { 
            state.performingAIHealthCheck = true;
        });
        builder.addCase(AIHealthCheck.fulfilled, (state) => { 
            state.performingAIHealthCheck = false;
        });
        builder.addCase(AIHealthCheck.rejected, (state) => { 
            state.performingAIHealthCheck = false;
        });

        builder.addCase(AIPredict.pending, (state) => { 
            state.newPredictionLoading = true;
        });
        builder.addCase(AIPredict.fulfilled, (state, action) => { 
            state.newPredictionLoading = false;
            if (action.payload.success) {
                state.columns = [];
                state.newPredictionDialogOpen = false;
                state.selectedColumn = null;
                state.selectedInputColumn = null;
                state.inputColumns = [];
                state.successfulNewPredictionReponse = action.payload.result;
                state.successfulNewPredictionOpen = true;
            }
        });
        builder.addCase(AIPredict.rejected, (state) => { 
            state.newPredictionLoading = false;
        });

        builder.addCase(DeletePredictionResult.pending, (state) => { 
            state.deletingPredictionResult = true;
        });
        builder.addCase(DeletePredictionResult.fulfilled, (state) => { 
            state.deletingPredictionResult = false;
            state.deletePredicitionResultDialogOpen = false;
            state.deletePredicitionResultBodyParams = null;
            state.refreshSelectedMLTesterTable++;
        });
        builder.addCase(DeletePredictionResult.rejected, (state) => { 
            state.deletingPredictionResult = false;
            state.deletePredicitionResultDialogOpen = false;
            state.deletePredicitionResultBodyParams = null;
            state.refreshSelectedMLTesterTable++;
        });

        builder.addCase(GetPredictionResultDetails.pending, (state) => { 
            state.predictionResultDeatilsLoading = true;
        });
        builder.addCase(GetPredictionResultDetails.fulfilled, (state, action) => { 
            state.predictionResultDeatilsLoading = false;
            if (action.payload?.success) {
                state.predictionResultDetails = action.payload.result;
            }
        });
        builder.addCase(GetPredictionResultDetails.rejected, (state) => { 
            state.predictionResultDeatilsLoading = false;
        });
    },
});

// Export actions for dispatching
export const { 
    handleToggleImportTrainingDataDialog,
    handleSetChosenFile,
    handleSetChosenName,
    handleToggleDeleteFilePopup,
    handleToggleFileDetailsPopup,
    handleToggleFileDatasetPopup,
    handleTesterRowClick,
    handleSelectColumn, 
    handleSelectInputColumn,
    handleCloseSuccessfulNewPredictionDetails,
    handleNewPrediction,
    handleMoveColumnToInput,
    handleMoveColumnToColumns,
    handleMoveAllColumnsToInput,
    handleMoveAllColumnsToColumns,
    handleToggleDeletePredictionResult,
    handleTogglePredictionResultDetailsDialog,
    handleRefreshMLTesterTable,
    handleRefreshSelectedMLTesterTable
} = mlTesterSlice.actions;

// Selector to access ML tester slice state
export const mlTesterSelector = (state: RootState) => state.mltester;